import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import CheckIcon from "@material-ui/icons/Check";
import { getTokens, INK } from "../../styles/tokens";
import { MONO } from "./ui";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    // Bloco de código é sempre escuro (cores de sintaxe/terminal são "cor de dado");
    // só a moldura acompanha o tema.
    root: {
      position: "relative",
      backgroundColor: INK[900],
      border: `1px solid ${t.mode === "dark" ? t.lineStrong : INK[800]}`,
      borderRadius: t.radius.sm,
      overflow: "hidden",
      minWidth: 0,
    },
    bar: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "6px 8px 6px 14px",
      borderBottom: "1px solid rgba(255,255,255,0.06)",
      minHeight: 36,
    },
    title: {
      fontFamily: t.font,
      fontSize: 12,
      fontWeight: 600,
      color: "rgba(229,233,242,0.72)",
      flex: 1,
      minWidth: 0,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    tabs: {
      display: "flex",
      gap: 2,
      flexWrap: "wrap",
    },
    tab: {
      fontFamily: t.font,
      fontSize: 12,
      fontWeight: 500,
      padding: "4px 10px",
      borderRadius: t.radius.badge,
      border: "none",
      background: "transparent",
      color: "rgba(229,233,242,0.72)",
      cursor: "pointer",
      transition: "background-color 150ms ease, color 150ms ease",
      "&:hover": { color: "#fff", backgroundColor: "rgba(255,255,255,0.06)" },
      "&:focus-visible": { outline: "2px solid rgba(255,255,255,0.6)", outlineOffset: 1 },
    },
    tabActive: {
      color: "#fff",
      backgroundColor: "rgba(255,255,255,0.12)",
    },
    copy: {
      color: "rgba(229,233,242,0.78)",
      padding: 6,
      "&:hover": { color: "#fff", backgroundColor: "rgba(255,255,255,0.08)" },
    },
    copied: { color: "#4ade80" },
    // fora do bloco escuro (ex.: linha do token) o botão segue o tema
    copyPlain: {
      padding: 6,
      color: t.textSecondary,
      "&:hover": { color: t.text },
    },
    copiedPlain: { color: t.semantic.success },
    pre: {
      margin: 0,
      padding: "14px 16px",
      fontFamily: MONO,
      fontSize: 12.5,
      lineHeight: 1.6,
      color: "#e5e9f2",
      overflowX: "auto",
      whiteSpace: "pre",
      tabSize: 2,
      maxHeight: 420,
      overflowY: "auto",
      "&::-webkit-scrollbar": { height: 8, width: 8 },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "rgba(255,255,255,0.18)",
        borderRadius: t.radius.sm,
      },
    },
    wrap: { whiteSpace: "pre-wrap", wordBreak: "break-word" },
  };
});

export const copyText = async (text) => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    /* fallback abaixo */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    return true;
  } catch (e) {
    return false;
  }
};

export const CopyButton = ({ text, className, label = "Copiar", plain = false }) => {
  // TODO i18n
  const classes = useStyles();
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!done) return undefined;
    const id = setTimeout(() => setDone(false), 1600);
    return () => clearTimeout(id);
  }, [done]);
  return (
    <Tooltip title={done ? "Copiado" : label}>
      <IconButton
        size="small"
        className={[
          plain ? classes.copyPlain : classes.copy,
          done ? (plain ? classes.copiedPlain : classes.copied) : null,
          className,
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={async () => {
          if (await copyText(text)) setDone(true);
        }}
        aria-label={label}
      >
        {done ? <CheckIcon fontSize="small" /> : <FileCopyOutlinedIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
};

/**
 * Bloco de código escuro (ink-900) com botão copiar e abas opcionais.
 *
 * Props: `code` (string) ou `codes` ({ key: string }) + `tabs` ([{ key, label }]),
 * `title?`, `wrap?` (quebra de linha), `value?/onChange?` para aba controlada.
 */
const CodeBlock = ({ code, codes, tabs, title, wrap, value, onChange, maxHeight }) => {
  const classes = useStyles();
  const [inner, setInner] = useState(tabs && tabs.length ? tabs[0].key : null);
  const active = value != null ? value : inner;
  const setActive = (k) => (onChange ? onChange(k) : setInner(k));
  const text = codes ? codes[active] || "" : code || "";

  return (
    <div className={classes.root}>
      <div className={classes.bar}>
        {title && <span className={classes.title}>{title}</span>}
        {tabs && tabs.length > 0 && (
          <div className={classes.tabs} role="tablist">
            {tabs.map((tb) => (
              <button
                key={tb.key}
                type="button"
                role="tab"
                aria-selected={active === tb.key}
                className={[classes.tab, active === tb.key ? classes.tabActive : null]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => setActive(tb.key)}
              >
                {tb.label}
              </button>
            ))}
          </div>
        )}
        {!title && !(tabs && tabs.length) && <span style={{ flex: 1 }} />}
        <CopyButton text={text} />
      </div>
      <pre
        className={[classes.pre, wrap ? classes.wrap : null].filter(Boolean).join(" ")}
        style={maxHeight ? { maxHeight } : undefined}
      >
        {text}
      </pre>
    </div>
  );
};

export default CodeBlock;
